import React, { useState, useMemo } from "react";

import {
  Button,
  Checkbox,
  Grid,
  FormControl,
  FormLabel,
  RadioGroup,
  FormControlLabel,
  Radio,
  Typography,
  TextInput,
  FloatInput,
  NumberInput,
  QuoteUrgency,
} from "@portex-pro/ui-components";
import FormStringConcat from "@portex-pro/ui-components/Forms/FormStringConcat";
import { FtlTrailerType, DriverPreference } from "api/types/generated-types";
import { ShipmentPackagingType, ShipmentPalletType, ShipmentWeightUnit } from "app/pages/shipments/types/domain";
import { Guide } from "components/Guide";
import TemperatureInput from "components/TemperatureInput";
import ThrottledTextInput from "components/ThrottledTextInput";
import useLDFlag from "hooks/useLDFlag";
import { ResourceKey } from "i18next";
import capitalize from "lodash/capitalize";
import find from "lodash/find";
import startCase from "lodash/startCase";
import upperCase from "lodash/upperCase";
import { DateTime } from "luxon";
import FormSelectView from "pages/shipper/pages/request-quote/components/FormSelectView";
import Section from "pages/shipper/pages/request-quote/components/Section";
import { ftlTrailerTypeList } from "pages/shipper/pages/request-quote/pages/ftl/constants/ftlTrailerType";
import { getUrgencyOptions } from "pages/shipper/pages/request-quote/pages/ftl/utils/getUrgencyOptions";
import { Controller, useFormContext, useWatch } from "react-hook-form";
import { useTranslation, Trans } from "react-i18next";
import { getSizesForType } from "types/TrailerSize";
import { isTemperatureControlled } from "types/TrailerType";
import { deserializeNotes } from "utils/deserializeNotes";
import { toLuxonRelative } from "utils/toLuxonRelative";

import { ButtonGroup } from "../../_components";
import StepCollapse from "../../_components/StepCollapse";
import { useCreateStepCollapseProps } from "../../_hooks/useStepCollapse";
import { useQuoteRequestSelector } from "../../_store";
import { quoteRequestActions } from "../../_store/quoteRequestSliceV2";
import { QuoteStepIndex } from "../../types";
import { FinalSchema } from "../_schemas";
import { LoadAttributesState } from "../types";
import { CargoDetailsSection } from "./CargoDetailsSection";
import DriverPreferenceView from "./DriverPreferenceView";

type LoadAttributesTagProps = {
  trailer_type?: string;
  trailer_size?: string;
  total_weight?: number;
  weight_unit?: string;
  is_palletized?: boolean;
  packaging_type?: string;
};

const LoadAttributesStepTag = ({
  trailer_type,
  trailer_size,
  total_weight,
  weight_unit,
  is_palletized,
  packaging_type,
}: LoadAttributesTagProps) => {
  const { t } = useTranslation(["common", "shipper"]);

  if (!trailer_type && !trailer_size && !total_weight) {
    return <> </>;
  }

  const parts: string[] = [];

  // Add equipment size (e.g., "26' Dry")
  if (trailer_size && trailer_type) {
    const sizeText = t(`common:trailerSizes.${trailer_size}` as ResourceKey);
    const typeText = trailer_type === "DRY" ? "Dry" : startCase(trailer_type.toLowerCase());
    parts.push(`${sizeText} ${typeText}`);
  } else if (trailer_size) {
    const sizeText = t(`common:trailerSizes.${trailer_size}` as ResourceKey);
    parts.push(sizeText as string);
  } else if (trailer_type) {
    const typeText = trailer_type === "DRY" ? "Dry" : startCase(trailer_type.toLowerCase());
    parts.push(typeText);
  }

  let packingInformation = "";
  // Add weight and packing information (e.g., "with 2,000 lb of palletized boxes")
  if (total_weight && weight_unit) {
    let packingDescription = "";
    if (is_palletized && packaging_type) {
      const packingTypeText = t(`shipper:packingTypeMap.${packaging_type}` as ResourceKey) as string;
      packingDescription = `palletized ${packingTypeText.toLowerCase()}`;
    } else if (is_palletized) {
      packingDescription = "palletized cargo";
    } else if (packaging_type) {
      const packingTypeText = t(`shipper:packingTypeMap.${packaging_type}` as ResourceKey) as string;
      packingDescription = packingTypeText.toLowerCase();
    } else {
      packingDescription = "cargo";
    }

    const weightText = `${total_weight.toLocaleString()} ${weight_unit.toLowerCase()}`;

    packingInformation = `with ${weightText} of ${packingDescription}`;
  }

  if (parts.length === 0) {
    return <></>;
  }

  const displayText = parts.join(" ");

  return (
    <div className="inline-flex items-center bg-blue-50 rounded-md px-2 py-1">
      <span className="text-blue-600 font-bold mr-2 text-sm">{displayText}</span>
      <span className="text-blue-600 text-sm">{` ${packingInformation}`}</span>
    </div>
  );
};

export const LoadAttributesStep: React.FC<{ onSubmit: (data: FinalSchema) => void }> = ({ onSubmit }) => {
  const { t } = useTranslation(["common", "shipper"]);
  const enableFTLCargoGroups = useLDFlag("ftlCargoGroups");

  const setQuoteRequestAction = quoteRequestActions.useSetQuoteRequest();
  const setStepIndex = quoteRequestActions.useSetStepIndex();
  const { stepIndex } = useQuoteRequestSelector((store) => store.quoteRequestSliceV2);
  const { baseStepCollapseProps, setIsOpen } = useCreateStepCollapseProps({
    expectedStepIndex: QuoteStepIndex.LoadAttributes,
    name: t("shipper:loadAttributes"),
    number: 2,
  });

  const methods = useFormContext<FinalSchema>();
  const deadline_respond_at = useWatch({ control: methods.control, name: "deadline_respond_at" });
  const trailer_type = useWatch({ control: methods.control, name: "trailer_type" });
  const trailer_size = useWatch({ control: methods.control, name: "trailer_size" });
  const total_weight = useWatch({ control: methods.control, name: "total_weight" });
  const weight_unit = useWatch({ control: methods.control, name: "weight_unit" });
  const is_palletized = useWatch({ control: methods.control, name: "is_palletized" });
  const packaging_type = useWatch({ control: methods.control, name: "packaging_type" });
  const is_hazardous = useWatch({ control: methods.control, name: "is_hazardous" });

  const submit = (data: FinalSchema) => {
    onSubmit(data);
    setStepIndex(QuoteStepIndex.Partners);
    setIsOpen(false);
  };

  const [urgencyOptions] = useState(getUrgencyOptions());

  const deadlineDateTime = useMemo(() => {
    return deadline_respond_at ? DateTime.fromISO(deadline_respond_at) : null;
  }, [deadline_respond_at]);

  const selectedUrgencyOption = useMemo(() => {
    if (!deadlineDateTime) return undefined;
    return find(
      urgencyOptions,
      (option) =>
        option.value.toUTC().day === deadlineDateTime.toUTC().day &&
        option.value.toUTC().hour === deadlineDateTime.toUTC().hour
    );
  }, [urgencyOptions, deadlineDateTime]);

  const renderUrgencyLabel = (id: string | undefined) => {
    const option = find(urgencyOptions, { id });
    const fallback = toLuxonRelative(deadlineDateTime);
    return option
      ? t("shipper:urgencyLabel", {
          type: capitalize("Quote"),
          label: option.label,
        })
      : t("shipper:urgencyLabel", {
          type: capitalize("Quote"),
          label: fallback,
        });
  };

  return (
    <StepCollapse
      {...baseStepCollapseProps}
      tag={
        <LoadAttributesStepTag
          trailer_type={trailer_type}
          trailer_size={trailer_size}
          total_weight={total_weight}
          weight_unit={weight_unit}
          is_palletized={is_palletized}
          packaging_type={packaging_type}
        />
      }
      footer={
        stepIndex <= QuoteStepIndex.LoadAttributes ? (
          <div className="flex justify-between mt-2 w-full">
            <div />
            <Button
              variant="contained"
              color="primary"
              size="large"
              onClick={methods.handleSubmit(submit)}
              style={{ width: 150 }}
            >
              {t("common:next")}
            </Button>
          </div>
        ) : null
      }
    >
      <div className="flex flex-col w-full h-full overflow-auto">
        <Section title={t("shipper:shipmentDetailsStep.referenceNoUrgency")} width="100%">
          <Grid container spacing={2}>
            <Grid item sm={6}>
              <Controller
                control={methods.control}
                name="reference_number"
                render={({ field }) => (
                  <TextInput
                    fullWidth
                    label={
                      <Typography>
                        <Trans i18nKey="shipmentDetailsStep.referenceNoOptional" ns="shipper">
                          <strong>PO/Reference No. </strong>(optional)
                        </Trans>
                      </Typography>
                    }
                    margin="dense"
                    value={field.value ?? ""}
                    placeholder={t("shipper:shipmentDetailsStep.setReferenceNo")}
                    onChange={(e) => setQuoteRequestAction({ reference_number: e.target.value })}
                  />
                )}
              />
            </Grid>
            <Grid item sm={6}>
              <Controller
                control={methods.control}
                name="internal_reference_number"
                render={({ field }) => (
                  <TextInput
                    fullWidth
                    label={
                      <Typography>
                        <Trans i18nKey="shipmentDetailsStep.internalReferenceNumberOptional" ns="shipper">
                          <strong>Internal Reference Number</strong>
                        </Trans>
                      </Typography>
                    }
                    margin="dense"
                    value={field.value ?? ""}
                    onChange={(e) => setQuoteRequestAction({ internal_reference_number: e.target.value })}
                  />
                )}
              />
            </Grid>
            <Grid item sm={6}>
              <Controller
                control={methods.control}
                name="hide_internal_ref_till_dispatch"
                render={({ field }) => (
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={!!field.value}
                        onChange={(e) => {
                          field.onChange(e.target.checked);
                          setQuoteRequestAction({ hide_internal_ref_till_dispatch: e.target.checked });
                        }}
                      />
                    }
                    label={t("shipper:shipmentDetailsStep.hideInternalRefTillDispatch")}
                  />
                )}
              />
            </Grid>
            <Grid item sm={6}>
              <Controller
                control={methods.control}
                name="deadline_respond_at"
                render={() => (
                  <FormControl fullWidth margin={"dense"}>
                    <FormLabel>{t("shipper:urgency")}</FormLabel>
                    <QuoteUrgency
                      data={urgencyOptions}
                      getLabel={renderUrgencyLabel}
                      value={selectedUrgencyOption?.id}
                      onChange={(_id, item) => setQuoteRequestAction({ deadline_respond_at: item.value.toISO() })}
                      onClear={() => setQuoteRequestAction({ deadline_respond_at: null })}
                    />
                  </FormControl>
                )}
              />
            </Grid>
          </Grid>
        </Section>

        <Section title={t("shipper:loadAttributesStep.trucksAndTrailers")}>
          <Grid container spacing={2}>
            <Grid item sm={3}>
              <Controller
                control={methods.control}
                name="truck_quantity"
                render={({ field }) => (
                  <NumberInput
                    translate="no"
                    label={t("shipper:loadAttributesStep.numberOfTrucks")}
                    value={field.value}
                    margin="dense"
                    fullWidth
                    onlyValidInput
                    disableError
                    validator="PositiveInt"
                    onChange={(e) => setQuoteRequestAction({ load_spec: { truck_quantity: Number(e.target.value) } })}
                  />
                )}
              />
            </Grid>
            <Grid item sm={3}>
              <Controller
                control={methods.control}
                name="driver_preference"
                render={({ field }) => (
                  <DriverPreferenceView
                    driverPreference={field.value || DriverPreference.None}
                    onSelectDriverPreference={(preference) =>
                      setQuoteRequestAction({ load_spec: { driver_preference: preference } })
                    }
                  />
                )}
              />
            </Grid>
            <Grid item sm={3}>
              <Controller
                control={methods.control}
                name="trailer_type"
                render={({ field, fieldState }) => (
                  <FormSelectView
                    label={t("shipper:trailerType")}
                    highlight={!!fieldState.error}
                    value={field.value as unknown as FtlTrailerType}
                    onChange={(value) => setQuoteRequestAction({ load_spec: { trailer_type: value } })}
                    formControlProps={{ margin: "dense", fullWidth: true, required: true }}
                    items={ftlTrailerTypeList}
                    getItemCopy={(ftlTrailerType) => {
                      switch (ftlTrailerType) {
                        case FtlTrailerType.Rgn:
                          return "Removable Gooseneck (RGN)";
                        case FtlTrailerType.NaPartnersChoice:
                          return "N/A - Partner's Choice";
                        default: {
                          return startCase(capitalize(ftlTrailerType));
                        }
                      }
                    }}
                  />
                )}
              />
            </Grid>
            <Grid item sm={3}>
              <Controller
                control={methods.control}
                name="trailer_size"
                render={({ field, fieldState }) => (
                  <Guide showGuide={!field.value && !fieldState.error}>
                    <FormControl required margin="dense" fullWidth>
                      <FormLabel>{t("shipper:loadAttributesStep.trailerSize")}</FormLabel>
                      <ButtonGroup
                        className="guideGroup"
                        options={getSizesForType(trailer_type).map((trailerSize) => ({
                          value: trailerSize,
                          label: t(`common:trailerSizes.${trailerSize}`),
                        }))}
                        selected={field.value ? `${field.value}` : undefined}
                        onSelect={(size) => setQuoteRequestAction({ load_spec: { trailer_size: size } })}
                        hasError={!!fieldState.error}
                      />
                    </FormControl>
                  </Guide>
                )}
              />
            </Grid>
            {isTemperatureControlled(trailer_type) ? (
              <>
                <Grid item sm={3}>
                  <Controller
                    control={methods.control}
                    name="min_temp"
                    render={({ field }) => (
                      <FormControl fullWidth margin="dense">
                        <FormLabel>{t("shipper:minTemp")}</FormLabel>
                        <TemperatureInput
                          value={field.value ?? undefined}
                          onChange={(value) => setQuoteRequestAction({ load_spec: { min_temp: value ?? undefined } })}
                        />
                      </FormControl>
                    )}
                  />
                </Grid>
                <Grid item sm={3}>
                  <Controller
                    control={methods.control}
                    name="max_temp"
                    render={({ field }) => (
                      <FormControl fullWidth margin="dense">
                        <FormLabel>{t("shipper:maxTemp")}</FormLabel>
                        <TemperatureInput
                          value={field.value ?? undefined}
                          onChange={(value) => setQuoteRequestAction({ load_spec: { max_temp: value ?? undefined } })}
                        />
                      </FormControl>
                    )}
                  />
                </Grid>
              </>
            ) : null}
          </Grid>
        </Section>

        {enableFTLCargoGroups ? (
          <Section title={t("common:cargoDetails").toUpperCase()}>
            <CargoDetailsSection />
          </Section>
        ) : (
          <>
            <Section title={t("shipper:loadAttributesStep.packingInformation")}>
              <Grid container spacing={2}>
                <Grid item sm={6} lg={4}>
                  <Controller
                    control={methods.control}
                    name="is_palletized"
                    render={({ field }) => (
                      <FormControl margin="dense" fullWidth>
                        <FormLabel>{t("shipper:packingMethod")}</FormLabel>
                        <ButtonGroup
                          options={[
                            { value: "true", label: t("shipper:palletized") },
                            { value: "false", label: t("shipper:floorLoaded") },
                          ]}
                          selected={field.value ? "true" : "false"}
                          onSelect={(value) =>
                            setQuoteRequestAction({ load_spec: { is_palletized: value === "true" } })
                          }
                        />
                      </FormControl>
                    )}
                  />
                </Grid>

                {is_palletized ? (
                  <>
                    <Grid item sm={3}>
                      <Controller
                        control={methods.control}
                        name="pallet_type"
                        render={({ field, fieldState }) => (
                          <Guide showGuide={!field.value && !fieldState.error} key="pallet_type">
                            <FormSelectView
                              label={t("shipper:palletType_label")}
                              highlight={!!fieldState.error}
                              value={field.value as ShipmentPalletType}
                              onChange={(pallet_type) => setQuoteRequestAction({ load_spec: { pallet_type } })}
                              formControlProps={{ margin: "dense", fullWidth: true, required: true }}
                              items={Object.values(ShipmentPalletType)}
                              getItemCopy={(palletType) => t(`shipper:palletTypeMap.${palletType}` as ResourceKey)}
                            />
                          </Guide>
                        )}
                      />
                    </Grid>
                    <Grid item sm={3}>
                      <Controller
                        control={methods.control}
                        name="pallet_count"
                        render={({ field }) => (
                          <NumberInput
                            translate="no"
                            label={t("shipper:palletCount")}
                            value={field.value ?? undefined}
                            margin="dense"
                            fullWidth
                            onlyValidInput
                            disableError
                            validator="PositiveInt"
                            onChange={(e) =>
                              setQuoteRequestAction({ load_spec: { pallet_count: Number(e.target.value) } })
                            }
                          />
                        )}
                      />
                    </Grid>
                    <Grid item sm={4}>
                      <Controller
                        control={methods.control}
                        name="packaging_type"
                        render={({ field }) => (
                          <FormSelectView
                            onClear={() => setQuoteRequestAction({ load_spec: { packaging_type: undefined } })}
                            onChange={(packaging_type) => setQuoteRequestAction({ load_spec: { packaging_type } })}
                            label={t("shipper:packingType")}
                            value={field.value as ShipmentPackagingType}
                            placeholder={t("shipper:setPackingType")}
                            formControlProps={{ margin: "dense", fullWidth: true }}
                            items={Object.values(ShipmentPackagingType).filter(
                              (type: ShipmentPackagingType) => !type.toString().startsWith("PALLETS")
                            )}
                            getItemCopy={(packagingType) => t(`shipper:packingTypeMap.${packagingType}` as ResourceKey)}
                          />
                        )}
                      />
                    </Grid>
                    <Grid item sm={4}>
                      <Controller
                        control={methods.control}
                        name="packing_count"
                        render={({ field }) => (
                          <NumberInput
                            translate="no"
                            label={t(`shipper:packingCountLabelMap.${packaging_type || "GENERIC"}` as ResourceKey)}
                            value={field.value ?? undefined}
                            margin="dense"
                            fullWidth
                            onlyValidInput
                            disableError
                            validator="PositiveInt"
                            onChange={(e) =>
                              setQuoteRequestAction({ load_spec: { packing_count: Number(e.target.value) } })
                            }
                          />
                        )}
                      />
                    </Grid>
                    <Grid item sm={4}>
                      <Controller
                        control={methods.control}
                        name="unit_count"
                        render={({ field }) => (
                          <NumberInput
                            translate="no"
                            label={t("shipper:unitCount")}
                            value={field.value ?? undefined}
                            margin="dense"
                            fullWidth
                            onlyValidInput
                            disableError
                            validator="PositiveInt"
                            onChange={(e) =>
                              setQuoteRequestAction({ load_spec: { unit_count: Number(e.target.value) } })
                            }
                          />
                        )}
                      />
                    </Grid>
                  </>
                ) : (
                  <>
                    <Grid item sm={6} lg={3}>
                      <Controller
                        control={methods.control}
                        name="packaging_type"
                        render={({ field }) => (
                          <FormSelectView
                            onClear={() => setQuoteRequestAction({ load_spec: { packaging_type: undefined } })}
                            onChange={(value) => setQuoteRequestAction({ load_spec: { packaging_type: value } })}
                            label={t("shipper:packingType")}
                            value={field.value as ShipmentPackagingType}
                            placeholder={t("shipper:setPackingType")}
                            formControlProps={{ margin: "dense", fullWidth: true }}
                            items={Object.values(ShipmentPackagingType).filter(
                              (type: ShipmentPackagingType) => !type.toString().startsWith("PALLETS")
                            )}
                            getItemCopy={(packagingType) => t(`shipper:packingTypeMap.${packagingType}` as ResourceKey)}
                          />
                        )}
                      />
                    </Grid>
                    <Grid item sm={6} lg={3}>
                      <Controller
                        control={methods.control}
                        name="packing_count"
                        render={({ field }) => (
                          <NumberInput
                            translate="no"
                            label={t(`shipper:packingCountLabelMap.${packaging_type || "GENERIC"}` as ResourceKey)}
                            value={field.value ?? undefined}
                            margin="dense"
                            fullWidth
                            onlyValidInput
                            disableError
                            validator="PositiveInt"
                            onChange={(e) =>
                              setQuoteRequestAction({ load_spec: { packing_count: Number(e.target.value) } })
                            }
                          />
                        )}
                      />
                    </Grid>
                    <Grid item sm={6} lg={2}>
                      <Controller
                        control={methods.control}
                        name="unit_count"
                        render={({ field }) => (
                          <NumberInput
                            translate="no"
                            label={t("shipper:unitCount")}
                            value={field.value ?? undefined}
                            margin="dense"
                            fullWidth
                            onlyValidInput
                            disableError
                            validator="PositiveInt"
                            onChange={(e) =>
                              setQuoteRequestAction({ load_spec: { unit_count: Number(e.target.value) } })
                            }
                          />
                        )}
                      />
                    </Grid>
                  </>
                )}
              </Grid>
            </Section>
            <Section title={t("shipper:loadAttributesStep.unitsAndWeights")}>
              <Grid container spacing={2}>
                <Grid item sm={4}>
                  <Controller
                    control={methods.control}
                    name="total_weight"
                    render={({ field }) => (
                      <NumberInput
                        translate="no"
                        label={t("shipper:loadAttributesStep.totalShipmentWeight")}
                        value={field.value ?? undefined}
                        margin="dense"
                        fullWidth
                        onlyValidInput
                        validator="PositiveFloat"
                        onChange={(e) => setQuoteRequestAction({ load_spec: { total_weight: Number(e.target.value) } })}
                      />
                    )}
                  />
                </Grid>
                <Grid item sm={4}>
                  <Controller
                    control={methods.control}
                    name="weight_per_unit"
                    render={({ field }) => (
                      <NumberInput
                        translate="no"
                        label={t("shipper:loadAttributesStep.weightPerUnit")}
                        value={field.value ?? undefined}
                        margin="dense"
                        fullWidth
                        onlyValidInput
                        disableError
                        validator="PositiveFloat"
                        onChange={(e) =>
                          setQuoteRequestAction({ load_spec: { weight_per_unit: Number(e.target.value) } })
                        }
                      />
                    )}
                  />
                </Grid>
                <Grid item sm={4}>
                  <Controller
                    control={methods.control}
                    name="weight_unit"
                    render={({ field }) => (
                      <FormControl fullWidth margin="dense">
                        <FormLabel>&nbsp;</FormLabel>
                        <ButtonGroup
                          options={[
                            { value: ShipmentWeightUnit.Lb, label: "lb" },
                            { value: ShipmentWeightUnit.Kg, label: "kg" },
                          ]}
                          selected={field.value}
                          onSelect={(unit) =>
                            setQuoteRequestAction({ load_spec: { weight_unit: unit as ShipmentWeightUnit } })
                          }
                        />
                      </FormControl>
                    )}
                  />
                </Grid>
              </Grid>
            </Section>
          </>
        )}

        <Section title={upperCase(t("common:commodities"))}>
          <Grid container spacing={2}>
            <Grid item sm={6}>
              <Controller
                control={methods.control}
                name="commodities"
                render={({ field }) => (
                  <FormStringConcat
                    margin="dense"
                    label={t("common:commodities")}
                    onChange={(value) => setQuoteRequestAction({ load_spec: { commodities: value } })}
                    value={field.value ?? ""}
                  />
                )}
              />
            </Grid>
            <Grid item sm={6}>
              <Controller
                control={methods.control}
                name="is_hazardous"
                render={({ field }) => (
                  <FormControl fullWidth margin={"dense"}>
                    <FormLabel>{t("shipper:hazardousGoods")}</FormLabel>
                    <RadioGroup
                      row
                      aria-label="hazardous"
                      name={t("shipper:hazardousGoods")}
                      value={field.value}
                      style={{ marginLeft: "6px" }}
                    >
                      <FormControlLabel
                        value={false}
                        control={<Radio name="is_hazardous" />}
                        checked={!field.value}
                        onClick={() => setQuoteRequestAction({ is_hazardous: false })}
                        label={t("common:no")}
                      />
                      <FormControlLabel
                        value={true}
                        control={<Radio name="is_hazardous" />}
                        checked={!!field.value}
                        onClick={() => setQuoteRequestAction({ is_hazardous: true })}
                        label={t("common:yes")}
                      />
                    </RadioGroup>
                  </FormControl>
                )}
              />
            </Grid>
            {is_hazardous ? (
              <Grid item sm={6}>
                <Controller
                  control={methods.control}
                  name="hazardous_notes"
                  render={({ field, fieldState }) => (
                    <Guide showGuide={!field.value && !fieldState.error} key="hazardous_notes">
                      <TextInput
                        margin="dense"
                        multiline
                        translate="no"
                        rows={3}
                        label={t("shipper:shipmentDetailsStep.hazardousDetailsLabel")}
                        placeholder={t("shipper:shipmentDetailsStep.hazardousDetailsPlaceholder")}
                        fullWidth
                        value={field.value ?? ""}
                        onChange={(e) => setQuoteRequestAction({ hazardous_goods_details: e.target.value })}
                        required
                        highlight={!!fieldState.error}
                      />
                    </Guide>
                  )}
                />
              </Grid>
            ) : null}
            <Grid item sm={12}>
              <Controller
                control={methods.control}
                name="goods_value"
                render={({ field }) => (
                  <FloatInput
                    label={t("common:goodsValueOptional")}
                    startIcon={<span className="ml-2">$</span>}
                    value={field.value ?? 0}
                    placeholder={t("common:goodsValue")}
                    decimalPlace={2}
                    onChange={(value) => setQuoteRequestAction({ goods_value: value })}
                  />
                )}
              />
            </Grid>
          </Grid>
        </Section>

        <div className="px-6 py-6">
          <Controller
            control={methods.control}
            name="notes"
            render={({ field }) => (
              <ThrottledTextInput
                multiline
                translate="no"
                rows={3}
                label={t("common:additionalNotes")}
                placeholder={t("shipper:additionalNotesPlaceholder")}
                InputProps={{ style: { paddingTop: 0, paddingBottom: 0 } }}
                fullWidth
                value={deserializeNotes(field.value)}
                onChange={(value) => setQuoteRequestAction({ note: value })}
              />
            )}
          />
        </div>
      </div>
    </StepCollapse>
  );
};

export const formatLoadAttributesDescriptor = (
  loadAttributes: LoadAttributesState
): { primary: string; secondary?: string } => {
  const parts = [];

  if (loadAttributes.trailerType && loadAttributes.trailerSize) {
    parts.push(`${loadAttributes.truckQuantity}x ${loadAttributes.trailerType} (${loadAttributes.trailerSize})`);
  }

  if (loadAttributes.weight) {
    parts.push(`${loadAttributes.weight} ${loadAttributes.weightUnit}`);
  }

  if (loadAttributes.commodities) {
    const truncatedCommodities =
      loadAttributes.commodities.length > 30
        ? loadAttributes.commodities.substring(0, 30) + "..."
        : loadAttributes.commodities;
    parts.push(truncatedCommodities);
  }

  return {
    primary: parts.join(" • ") || "No details",
  };
};
